import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Some browsers/devtools can still prevent autoplay from starting even when `autoplay` is set.
    // Attempt an explicit play once the video has loaded enough data.
    const tryPlay = () => {
      const playResult = videoElement.play();
      // `play()` returns a Promise in modern browsers; ignore rejections (autoplay policies, etc.).
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => {});
      }
    };

    tryPlay();
    videoElement.addEventListener('loadeddata', tryPlay, { once: true });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
